/** @jsxImportSource react */
import { createContext, useContext, useMemo, useState } from 'react'
import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material'
import { createAppTheme } from './theme'

const ColorModeContext = createContext({ mode: 'light', toggle: () => {} })

export const useColorMode = () => useContext(ColorModeContext)

const readStoredMode = () => {
  try {
    return localStorage.getItem('colorMode')
  } catch {
    return null
  }
}

/** Follows the OS setting until the user picks a mode, which is then remembered. */
const ColorModeProvider = ({ children }) => {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)')
  const [stored, setStored] = useState(readStoredMode)
  const mode = stored || (prefersDark ? 'dark' : 'light')

  const value = useMemo(
    () => ({
      mode,
      toggle: () => {
        const next = mode === 'dark' ? 'light' : 'dark'
        setStored(next)
        try {
          localStorage.setItem('colorMode', next)
        } catch {
          // storage unavailable: mode still applies for this session
        }
      },
    }),
    [mode]
  )
  const theme = useMemo(() => createAppTheme(mode), [mode])

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        {/* enableColorScheme: native scrollbars and form controls follow light/dark mode */}
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  )
}

export default ColorModeProvider
